import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { BsChevronDown, BsChevronUp } from 'react-icons/bs';
import Dropdown, { Option } from 'react-dropdown';
import { useUserContext } from '../../context/UserContext';
import { RoomObjectType } from '../../types/room';
import { useConfigurationContext } from '../../context/ConfigurationContext';
import UploadButton from './UploadButton';
import { useRoomContext, useRoomsByOwner } from '../../context/RoomContext';
import {
  cssBackground,
  formatRoomName,
  getDisplayName,
} from '../../utils/room_utils';
import { useTargetContext } from '../../context/TargetContext';
import { useNFTContext } from '../../context/NFTContext';
import { OpenInNew } from '@mui/icons-material';
import { BASE_SCANNER_ENDPOINT } from '../../constants';

import EnterErrorDialog from './dialogs/EnterErrorDialog';
import EnterSuccessDialog from './dialogs/EnterSuccessDialog';
import EditSuccessDialog from './dialogs/EditSuccessDialog';
import EditErrorDialog from './dialogs/EditErrorDialog';

type Inputs = {
  roomName: string;
  roomDescription: string;
  roomVisitorLimit: number;
};

const baseLockTypeOptions: Option[] = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'password', label: 'Password' },
  { value: 'invisible', label: 'Invisible' },
  { value: 'noobs_only', label: 'Noobs Only' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'frozen', label: 'Frozen' },
  { value: 'friends', label: 'Friends' },
  { value: 'nft', label: 'NFT' },
];

const SidePanelRoomPreview = ({
  selectedNFT,
  handleStartPlacingNFT,
  onPanelClose,
}: {
  selectedNFT: RoomObjectType;
  handleStartPlacingNFT: (nft: RoomObjectType) => void;
  onPanelClose: () => void;
}) => {
  const [editRoomDetailsMode, setEditRoomDetailsMode] = useState(false);

  const { target } = useTargetContext();
  const { wallet_address, connected } = useUserContext();
  const { edit, enter } = useRoomContext();

  const { rooms } = useRoomsByOwner(connected ? wallet_address : '');

  const { visitorLimit, lockedStates } = useConfigurationContext();

  const { setSelectedNFT, setTempSelectedNFTImage } = useNFTContext();

  const { handleSubmit, register } = useForm();

  const lockTypeOptions = useMemo(
    () =>
      baseLockTypeOptions
        .map((lockType) => ({ ...lockType, disabled: true }))
        .filter((lockType) => lockedStates.includes(lockType.value)),
    [lockedStates]
  );

  const [selectedLockType, setSelectedLockType] = useState<Option | undefined>(
    lockTypeOptions.find((opt) => opt.value === selectedNFT.roomLockedState) ||
      lockTypeOptions[0]
  );

  const [imageFile, setImageFile] = useState<File | undefined>();
  const [imageSrc, setImageSrc] = useState<string | undefined>(
    selectedNFT.mapIconUrl
  );

  const onFileChange = useCallback(
    (file?: File) => {
      if (file) {
        const image = URL.createObjectURL(file);

        setImageSrc(image);
        setTempSelectedNFTImage(image);
        setImageFile(file);
      } else {
        setImageSrc(selectedNFT.mapIconUrl);
        setTempSelectedNFTImage('');
        setImageFile(undefined);
      }
    },
    [selectedNFT.mapIconUrl, setTempSelectedNFTImage]
  );

  const [savingChanges, setSavingChanges] = useState(false);
  const [showEditSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showEditErrorDialog, setShowErrorDialog] = useState(false);

  const onSubmit: SubmitHandler<Inputs> = useCallback(
    async (data: Inputs) => {
      setSavingChanges(true);

      const room = await edit(
        selectedNFT.roomId,
        data.roomName,
        data.roomDescription,
        data.roomVisitorLimit,
        selectedLockType?.value || 'open',
        imageFile
      );

      if ('result' in room) {
        target === 'dev' && console.log('Not placed due to error');
        setErrorDialogMessage(room.error);
        setShowErrorDialog(true);
      } else {
        target === 'dev' && console.log('Placed succesfully');
        setShowSuccessDialog(true);
        setSelectedNFT(room);
        setImageSrc(room.mapIconUrl);
      }

      setSavingChanges(false);
      setEditRoomDetailsMode(false);
    },
    [
      edit,
      imageFile,
      selectedLockType?.value,
      selectedNFT.roomId,
      setSelectedNFT,
      target,
    ]
  );

  const [showEnterRoomSuccessDialog, setShowEnterRoomSuccessDialog] =
    useState(false);
  const [showEnterRoomErrorDialog, setShowEnterRoomErrorDialog] =
    useState(false);

  const onEnterRoom = useCallback(async () => {
    setShowEnterRoomSuccessDialog(false);
    setShowEnterRoomErrorDialog(false);

    const result = await enter(selectedNFT.roomId);

    if (result) setShowEnterRoomSuccessDialog(true);
    else setShowEnterRoomErrorDialog(true);
  }, [enter, selectedNFT.roomId]);

  useEffect(() => {
    setEditRoomDetailsMode(false);
  }, [selectedNFT]);

  useEffect(() => {
    if (!editRoomDetailsMode) {
      setTempSelectedNFTImage('');
    }
  }, [editRoomDetailsMode, setTempSelectedNFTImage]);

  const isOwner = useMemo(
    () => rooms.map((room) => room.roomId).includes(selectedNFT.roomId),
    [rooms, selectedNFT.roomId]
  );

  const [errorDialogMessage, setErrorDialogMessage] = useState<
    string | undefined
  >(undefined);

  const actualRoomVisitorLimit = useMemo(
    () =>
      Math.min(
        Math.max(selectedNFT.roomVisitorLimit, visitorLimit.minVisitorLimit),
        visitorLimit.maxVisitorLimit
      ),
    [
      selectedNFT.roomVisitorLimit,
      visitorLimit.maxVisitorLimit,
      visitorLimit.minVisitorLimit,
    ]
  );

  if (!editRoomDetailsMode) {
    return (
      <>
        {showEditSuccessDialog && (
          <EditSuccessDialog onClose={() => setShowSuccessDialog(false)} />
        )}
        {showEditErrorDialog && (
          <EditErrorDialog
            message={errorDialogMessage}
            onClose={() => {
              setShowErrorDialog(false);
              setErrorDialogMessage(undefined);
            }}
          />
        )}
        {showEnterRoomSuccessDialog && (
          <EnterSuccessDialog
            onClose={() => setShowEnterRoomSuccessDialog(false)}
          />
        )}
        {showEnterRoomErrorDialog && (
          <EnterErrorDialog
            onClose={() => setShowEnterRoomErrorDialog(false)}
          ></EnterErrorDialog>
        )}
        <div
          className="side-panel-room-preview-image"
          style={cssBackground(selectedNFT)}
        ></div>
        <div className="side-panel-inner-container">
          {/* TODO: add author information (API doesn't return this yet.) */}
          {/* <div className="side-panel-user-container">
            <div
              className="side-panel-user-image"
              style={{
                backgroundImage: selectedNFT.author?.image
                  ? `url(${selectedNFT.author?.image}) no-repeat center/cover`
                  : undefined,
              }}
            />
            <div className="side-panel-user-info">
              <div>{selectedNFT.author?.user_name}</div>
              <div className="side-panel-room-modified">
                Minted
                {' ' + new Date(1679896894938).toLocaleDateString('fi-FI')}
              </div>
            </div>
          </div> */}
          {isOwner && (
            <>
              <button
                className="side-panel-action-button edit"
                onClick={() => setEditRoomDetailsMode(true)}
              >
                Edit room details
              </button>
              <button
                className="side-panel-action-button move"
                onClick={() => handleStartPlacingNFT(selectedNFT)}
              >
                Move room
              </button>
            </>
          )}
          <h2 className="side-panel-room-name">
            {formatRoomName(selectedNFT)}
          </h2>
          <div className="side-panel-token">
            <div className="side-panel-token-title">
              {getDisplayName(selectedNFT.contractAddress)} #
              {selectedNFT.tokenId}
            </div>
            <a
              href={`${BASE_SCANNER_ENDPOINT[target]}/nft/${selectedNFT.contractAddress}/${selectedNFT.tokenId}`}
              target="_blank"
              className="side-panel-token-contract"
              rel="noreferrer"
            >
              {selectedNFT.contractAddress.slice(0, 5)}...
              {selectedNFT.contractAddress.slice(-3)}&nbsp;
              <OpenInNew />
            </a>
          </div>
          <h3 className="side-panel-description-title">Description</h3>
          <div className="side-panel-room-description">
            {selectedNFT.roomDescription?.trim() || (
              <i>This room has no description.</i>
            )}
          </div>
          <h3 className="side-panel-description-title">Visitor limit</h3>
          <div className="side-panel-room-description">
            {actualRoomVisitorLimit}
          </div>
          <h3 className="side-panel-description-title">Room access</h3>
          <div className="side-panel-room-description">
            {selectedNFT.roomLockedState}
          </div>
          <h3 className="side-panel-description-title">Location</h3>
          <div className="side-panel-room-description">
            Floor {selectedNFT.roomFloor} at x{selectedNFT.startX} y
            {selectedNFT.startY}
          </div>
          {target === 'dev' && (
            <>
              <h3 className="side-panel-description-title">Room Id</h3>
              <div className="side-panel-room-description">
                {selectedNFT.roomId}
              </div>
            </>
          )}
          {selectedNFT.tags && (
            <div className="side-panel-room-tags-container">
              {selectedNFT.tags.map((tag) => (
                <div className="side-panel-room-tag" key={tag}>
                  {tag}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="side-panel-action-buttons-container">
          <button
            aria-label="Coming soon."
            className={`side-panel-action-button grow ${
              (selectedNFT.roomLockedState !== 'open' && !isOwner) || !connected
                ? 'disabled'
                : ''
            }`}
            disabled={
              (selectedNFT.roomLockedState !== 'open' && !isOwner) || !connected
            }
            onClick={onEnterRoom}
          >
            Enter room
          </button>
          <button
            className="side-panel-action-button close"
            onClick={() => onPanelClose()}
          >
            Close
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className="side-panel-room-preview-image"
        style={cssBackground(selectedNFT, imageSrc || selectedNFT.mapIconUrl)}
      >
        <UploadButton onFile={onFileChange} />
      </div>
      <div className="side-panel-inner-container">
        <h2 className="side-panel-room-name">Edit room</h2>
        <form
          id="edit-room-details-form"
          className="edit-room-details-form"
          // @ts-expect-error
          onSubmit={handleSubmit(onSubmit)}
        >
          <label>
            Room name
            <input
              type="text"
              defaultValue={formatRoomName(selectedNFT)}
              {...register('roomName', { required: true })}
            />
          </label>
          <label>
            Description
            <textarea
              defaultValue={selectedNFT.roomDescription}
              {...register('roomDescription', { required: true })}
            />
          </label>
          <label>
            Visitor limit
            <input
              type="number"
              defaultValue={actualRoomVisitorLimit}
              min={visitorLimit.minVisitorLimit}
              max={visitorLimit.maxVisitorLimit}
              {...register('roomVisitorLimit', { required: true })}
            />
          </label>
          <label>
            Lock type
            <Dropdown
              arrowClosed={<BsChevronDown />}
              arrowOpen={<BsChevronUp />}
              controlClassName="basic-dropdown"
              options={lockTypeOptions}
              onChange={setSelectedLockType}
              value={selectedLockType}
              placeholder={'Open'}
            />
          </label>
        </form>
      </div>
      <div className="side-panel-action-buttons-container">
        <button
          className={`side-panel-action-button grow ${
            savingChanges ? 'disabled' : ''
          }`}
          type="submit"
          form="edit-room-details-form"
        >
          Save changes
        </button>
        <button
          className="side-panel-action-button close"
          onClick={() => setEditRoomDetailsMode(false)}
        >
          Cancel
        </button>
      </div>
    </>
  );
};

export default SidePanelRoomPreview;
